/**
 * An inserted block lands in place of an empty paragraph rather than after it, so building a
 * document from the palette does not leave a trail of blank lines. Replacing (never deleting)
 * keeps every container non-empty.
 */
import type { Node as PMNode, ResolvedPos } from '@tiptap/pm/model'
import { NodeSelection, type Selection } from '@tiptap/pm/state'

export interface BlockRange { from: number; to: number }

function emptyParagraphAround($pos: ResolvedPos): BlockRange | null {
  const parent = $pos.parent
  if ($pos.depth < 1 || parent.type.name !== 'paragraph' || parent.content.size !== 0) return null
  return { from: $pos.before(), to: $pos.after() }
}

/** The empty paragraph holding a collapsed cursor, or null. */
export function emptyParagraphAtSelection(selection: Selection): BlockRange | null {
  if (!selection.empty || selection instanceof NodeSelection) return null
  return emptyParagraphAround(selection.$from)
}

/** The empty paragraph a drop at `pos` lands in (or directly on), or null. */
export function emptyParagraphAtDrop(doc: PMNode, pos: number): BlockRange | null {
  const $pos = doc.resolve(pos)
  const inside = emptyParagraphAround($pos)
  if (inside) return inside
  const after = $pos.nodeAfter
  if (after && after.type.name === 'paragraph' && after.content.size === 0 && !$pos.parent.inlineContent) {
    return { from: pos, to: pos + after.nodeSize }
  }
  return null
}
